import PhoneOutlined from '@mui/icons-material/PhoneOutlined'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import React, { useEffect, useRef, useState } from 'react'
import { useInView } from 'src/hooks/useInView'
import { ILocation } from 'src/types/graphql'
import { numberWithCommas } from 'src/utils'
import {
  buildDirectoryFichaHref,
  directoryBrandColor,
  directoryMonogram,
  displayableDirectoryHours,
  sortDirectorySizes
} from 'src/utils/directory'
import { prettifyDirectoryText } from 'src/utils/directoryText'
import {
  DirectorySurface,
  trackDirectoryCardClick,
  trackDirectoryImpression,
  trackDirectoryPhoneOpen
} from 'src/utils/pixels/directoryEvents'

/**
 * The reveal form carries react-hook-form, the phone field and the BotID
 * pre-flight. This card is statically imported by every search page (through
 * `SearchList` → `DirectoryZeroSupply`), so the form loads only on the tap
 * that opens it — never on a page paid traffic lands on.
 */
const RevealContact = dynamic(() => import('./RevealContact'), { ssr: false })

/** ink-200 — the neutral tile for a facility with no scraped brand colour. */
const DEFAULT_BRAND_COLOR = '#4F6A7E'

interface DirectoryGuideCardProps {
  location: ILocation
  /** 1-based rank within the guide, for position-aware CTR. */
  position: number
  /** Zero-supply page (the guide is the answer) or weak-supply page (the
   *  guide follows a few SpotMe listings) — required for analytics. */
  surface: Extract<
    DirectorySurface,
    'search_zero_supply' | 'search_weak_supply'
  >
}

/**
 * One facility in the ZERO-SUPPLY city guide (2026-10-20, Jules: "give them
 * the presence and usability of SpotMe listings").
 *
 * Where it differs from `DirectoryCard` (the muted tier-floor card under real
 * results), and why:
 *   · Full weight, white surface. On this page there is no monetized supply to
 *     dilute — the guide IS the answer, so it should read like one.
 *   · The phone is one tap away: "Ver teléfono" opens the same one-field
 *     reveal the ficha uses, inline, so the renter never has to leave the city
 *     page. The cap, the lead capture and the ledger are untouched — this is a
 *     second door into the same `revealDirectoryContact` mutation.
 *   · DENUE capitals and registry filler are cleaned for display.
 *
 * What does NOT change — the PROFECO firewall (directory plan, "Legal frame"
 * §7): the "Directorio · No verificado" chip stays on every card, no rating,
 * no availability, no "Reservar". A guide card must never be mistaken for a
 * bookable SpotMe listing.
 *
 * The card is NOT one big link: it holds a button, and a button inside an
 * anchor is invalid markup that breaks keyboard and screen-reader use. The
 * name and "Ver ficha" are the two links; both point at the canonical ficha
 * path, because this markup is server-rendered and is the crawl path into the
 * city's fichas.
 */
const DirectoryGuideCard = ({
  location,
  position,
  surface
}: DirectoryGuideCardProps) => {
  const { t } = useTranslation('directorio')
  const [isRevealOpen, setIsRevealOpen] = useState(false)
  const revealRef = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLElement>(null)
  const inView = useInView(ref, { threshold: 0.5 })

  const { id, directoryMeta, coordinates, directoryHasContact } = location
  const href = buildDirectoryFichaHref(location)
  const name = prettifyDirectoryText(location.title)
  const address = prettifyDirectoryText(
    location.address || location.cardLocation || location.shortAddress
  )
  const brandColor = directoryBrandColor(location) ?? DEFAULT_BRAND_COLOR
  const monogram = directoryMonogram(location)

  const priceFrom = directoryMeta?.priceFromMxn
  const sizes = directoryMeta?.sizes?.trim()
  const hours = displayableDirectoryHours(directoryMeta?.hours)
  const facts = [
    priceFrom
      ? t(
          directoryMeta?.priceExcludesIva ? 'CARD.FROM_PLUS_IVA' : 'CARD.FROM',
          {
            price: numberWithCommas(priceFrom)
          }
        )
      : null,
    sizes ? sortDirectorySizes(sizes) : null
  ].filter((fact): fact is string => !!fact)

  useEffect(() => {
    if (!inView) return
    trackDirectoryImpression({
      surface,
      locationId: id,
      position
    })
  }, [inView, id, position, surface])

  // The button that opened the form unmounts with the tap, so focus would fall
  // back to <body> and a screen reader would announce nothing. Hand it to the
  // form region instead.
  useEffect(() => {
    if (isRevealOpen) revealRef.current?.focus()
  }, [isRevealOpen])

  const onFichaClick = () =>
    trackDirectoryCardClick({
      surface,
      locationId: id,
      position
    })

  return (
    <article
      ref={ref}
      className="@container rounded-2xl border border-ink-100/15 bg-white-500 p-4 transition-colors duration-200 hover:border-ink-100/30 @sm:p-5"
      data-testid="directory-guide-card"
    >
      <div className="flex items-start gap-3 @sm:gap-4">
        {/* Same SVG brand tile as the floor card — the scraped colour rides on
            a `fill` attribute, never an inline style, on this SSR'd markup. */}
        <svg
          viewBox="0 0 36 36"
          className="size-11 shrink-0 @sm:size-12"
          aria-hidden="true"
          focusable="false"
        >
          <rect width="36" height="36" rx="9" fill={brandColor} />
          <text
            x="18"
            y="18"
            textAnchor="middle"
            dominantBaseline="central"
            fill="#FFFFFF"
            fontSize="16"
            fontWeight="500"
          >
            {monogram}
          </text>
        </svg>

        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center rounded-full bg-ink-100/10 px-2 py-0.5 text-[10px] font-medium tracking-[0.02em] whitespace-nowrap text-ink-200 uppercase">
            {t('CHIP')}
          </span>
          <h3 className="mt-1.5 font-display text-[16px] leading-snug font-medium text-ink-700 @sm:text-[17px]">
            {href ? (
              <Link
                href={href}
                onClick={onFichaClick}
                className="underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700"
              >
                {name}
              </Link>
            ) : (
              name
            )}
          </h3>
          {address && (
            <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-ink-200">
              {address}
            </p>
          )}
          {(facts.length > 0 || hours) && (
            <dl className="mt-2 flex flex-col gap-0.5 text-[13px] text-ink-700">
              {facts.length > 0 && (
                <div className="flex gap-1.5">
                  <dt className="sr-only">{t('GUIDE.FACTS_LABEL')}</dt>
                  <dd>{facts.join(' · ')}</dd>
                </div>
              )}
              {hours && (
                <div className="flex min-w-0 gap-1.5">
                  <dt className="shrink-0 text-ink-200">
                    {t('GUIDE.HOURS_LABEL')}
                  </dt>
                  <dd className="line-clamp-1 min-w-0">{hours}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
      </div>

      {/* Two short labels: stacked below a 320px card, side by side above it.
          A container query, not `md:` — on desktop this card sits in the
          ~357px results rail, narrower than most phones. */}
      <div className="mt-3 flex flex-col gap-2 @xs:flex-row @xs:items-center @xs:gap-4">
        {directoryHasContact && !isRevealOpen && (
          <button
            type="button"
            onClick={() => {
              setIsRevealOpen(true)
              trackDirectoryPhoneOpen({ locationId: id, position })
            }}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-blue-800 px-5 py-2 text-sm font-medium text-white-500 transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700 active:translate-y-0 motion-reduce:transition-none"
          >
            <PhoneOutlined className="text-[17px]!" aria-hidden="true" />
            {t('GUIDE.PHONE_CTA')}
          </button>
        )}
        {href && (
          <Link
            href={href}
            onClick={onFichaClick}
            aria-label={t('CARD.A11Y_LABEL', { name, chip: t('CHIP') })}
            className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-blue-800 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700"
          >
            {t('CARD.CTA')}
            <i className="si si-right text-[8px]" aria-hidden="true" />
          </Link>
        )}
      </div>

      {isRevealOpen && (
        <div
          ref={revealRef}
          tabIndex={-1}
          aria-label={t('GUIDE.A11Y_REVEAL_REGION', { name })}
          className="mt-3 rounded-2xl outline-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <RevealContact
            locationId={id}
            // On this page there is no verified supply by definition — the
            // capped state offers WhatsApp instead of an empty results page.
            verifiedNearbyHref={null}
            coordinates={coordinates}
            surface={surface}
            headingLevel="h4"
          />
        </div>
      )}
    </article>
  )
}

export default React.memo(DirectoryGuideCard)
